/**
 * Runway-designator synthesis for imported (aeroway) geometry.
 *
 * Each runway feature carries a `ref` like "17/35" and two stitched end nodes.
 * We assign each physical end its designator by matching the parsed refs to the
 * end's *landing* heading (designator × 10°), then build an authorable
 * `TaxiRunway`: the longest runway is primary (live), the rest are authored but
 * off so the editor can switch the active config. Land toward the lower-numbered
 * end, depart from the higher (e.g. 17/35 → land 17, depart 35).
 */
import { bearingDeg } from '../motion/easing'
import { mkRunway, type TaxiRunway } from './taxiGraph'

type Pt = [number, number]
const dist = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1])
const M_TO_FT = 3.28084

/** A runway from the importer: `ref`/width/surface plus its two stitched ends. */
export interface RunwayFeature {
  ref?: string
  widthM?: number
  surface?: string
  endA: { pos: Pt }
  endB: { pos: Pt }
}

/**
 * Best designator for a landing `heading`: the parsed ref whose `designator×10°`
 * is closest to the heading (mod 360), else one derived straight from the heading.
 */
export function designatorFor(refs: string[], heading: number): string {
  let best = refs[0] ?? String(Math.round(((heading % 360) + 360) % 360 / 10) || 36).padStart(2, '0')
  let bestErr = Infinity
  for (const r of refs) {
    const n = Number(r.replace(/[^0-9]/g, ''))
    if (!Number.isFinite(n)) continue
    const diff = Math.abs((((n * 10 - heading) % 360) + 540) % 360 - 180)
    if (diff < bestErr) {
      bestErr = diff
      best = r
    }
  }
  return best
}

/** Build authorable runways from the imported runway features (longest = primary). */
export function synthesizeImportedRunways(features: RunwayFeature[], groupId: string): TaxiRunway[] {
  let longestLen = 0
  for (const rf of features) longestLen = Math.max(longestLen, dist(rf.endA.pos, rf.endB.pos))
  return features.map((rf) => {
    const refs = (rf.ref ?? '').split('/').map((s) => s.trim()).filter(Boolean)
    // Landing heading flown to touch down AT each end = direction of travel after
    // crossing that threshold (toward the far end).
    const headToA = bearingDeg(rf.endB.pos, rf.endA.pos)
    const headToB = bearingDeg(rf.endA.pos, rf.endB.pos)
    const desigA = designatorFor(refs, headToA)
    const desigB = designatorFor(refs, headToB)
    const isPrimary = longestLen > 0 && dist(rf.endA.pos, rf.endB.pos) >= longestLen - 1
    return mkRunway(
      groupId,
      rf.ref ?? `${desigA}/${desigB}`,
      { designator: desigA, threshold: [rf.endA.pos[0], rf.endA.pos[1]] },
      { designator: desigB, threshold: [rf.endB.pos[0], rf.endB.pos[1]] },
      {
        widthFt: rf.widthM ? Math.round(rf.widthM * M_TO_FT) : 150,
        surface: rf.surface,
        // Primary runway is live; others authored-but-off (switchable in the editor).
        active: { mode: 'always', enabled: isPrimary },
        // Land toward the lower-numbered end, depart from the higher-numbered end.
        endA: { useForLanding: Number(desigA.replace(/[^0-9]/g, '')) <= Number(desigB.replace(/[^0-9]/g, '')), useForTakeoff: Number(desigA.replace(/[^0-9]/g, '')) > Number(desigB.replace(/[^0-9]/g, '')) },
        endB: { useForLanding: Number(desigB.replace(/[^0-9]/g, '')) < Number(desigA.replace(/[^0-9]/g, '')), useForTakeoff: Number(desigB.replace(/[^0-9]/g, '')) >= Number(desigA.replace(/[^0-9]/g, '')) },
        flags: { imported: true },
      }
    )
  })
}
